import React, { useCallback } from "react";
import { GroupMember, actions, useAppDispatch, useAppSelector, withAsyncThunkErrorHandling } from "@lib/store";
import { Avatar, IconButton } from "@molecules";
import { H4, Subtitle } from "@quarks";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { useDialog } from "@hooks";

export const GroupMemberItem = ({ member, groupId }: { member: GroupMember; groupId: string }) => {
  const { t } = useTranslation();
  const currentUserId = useAppSelector(state => state.data.user.user.id);
  const { destructiveDialog } = useDialog();

  const dispatch = useAppDispatch();

  const isUpdatingStatus = useAppSelector(state => {
    return state.data.groups.pendingMutations.some(
      mutation =>
        mutation.type === "patch" && mutation.data.groupId === groupId && mutation.data.memberId === member.user.id,
    );
  });

  const handleRemove = useCallback(async () => {
    const confirmed = await destructiveDialog(
      t("groups:RemoveMember.DialogTitle"),
      t("groups:RemoveMember.DialogMessage"),
      undefined,
      t("groups:RemoveMember.ConfirmButtonLabel"),
    );
    if (!confirmed) return;

    const res = await dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.removeMemberFromGroup({
          groupId: groupId,
          memberId: member.user.id,
        }),
      ),
    );

    res.success && toast.success(t("groups:RemoveMember.SuccessToast", { name: member.user.name }));
  }, [destructiveDialog, dispatch, groupId, member.user.name, member.user.id, t]);

  return (
    <div className={`grid grid-cols-[auto_1fr_auto] gap-x-4 items-center w-full py-2 text-left`}>
      <Avatar user={member.user} />

      <div className="grid min-w-0">
        <H4 className="truncate">
          {member.user.name}{" "}
          {currentUserId === member.user.id && <span className="text-mapiq-black-500">({t("translation:You")})</span>}
        </H4>
        <Subtitle className="text-sm truncate">{member.user.email}</Subtitle>
      </div>
      {member.user.id !== currentUserId && (
        <IconButton
          size="xsmall"
          disabled={isUpdatingStatus}
          onClick={handleRemove}
          iconButton={"tertiary"}
          loading={isUpdatingStatus}
          icon={"trash"}
        />
      )}
    </div>
  );
};
